import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronDown, ChevronUp, UserPlus } from 'lucide-react';
import { usePatient } from '../../context/patientContext';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useHasAllPermissions } from '../../hooks/usePermission';
import { useDebounce } from '../../hooks/useDebounce';
import { fetchPatients, fetchMyPatients, normalizePatientStatus } from '../../services/admin/patient.service';
import { PatientData } from '../../types/admin/patient';
import type { PatientStatusWire } from '../../dtos/admin/patient.dto';
import type { PermissionString } from '../../types/permissions';
import { DEFAULT_PATIENT_STATUS_FILTER, PATIENT_STATUS_FILTERS } from '../../constants/patientLifecycle';
import { LoadingSpinner, PatientAvatar, StatusPill } from '../common';
import AllPatientsList from './AllPatientsList';
import PayerBadges from './insurance/PayerBadges';
import RecertOverdueBadge from './RecertOverdueBadge';
import type { InsuranceSummary } from '../../dtos/patientmr/insurance.dto';
import IntakeDialog from './intake/IntakeDialog';
import CapUsageBanner from '../subscription/CapUsageBanner';
import { SubscriptionService } from '../../services/billing/subscription.service';
import { openPatientChartById } from '../../constants/navigationEvents';
import { describeApiError, getApiErrorCode } from '../../utils/apiErrors';
import { isApiError } from '../../dtos/common/api.dto';

// Define the Patient interface
interface Patient {
  id: number | string;
  name?: string;
  firstName?: string;
  lastName?: string;
  dob: string;
  gender: string;
  mrn: string;
  status?: string;
  initials?: string;
  profilePictureUrl?: string | null;
  insuranceSummary?: InsuranceSummary | null;
  recertOverdue?: boolean;
}

interface PatientHomeProps {
  currentUserEmail?: string;
  onSelectPatient?: (patient: Patient | PatientData) => void;
  onShowPatientChart?: (patient: Patient | PatientData) => void; // Add back the prop for interface compatibility
  selectedPatientId?: number | string; // To track which patient is currently selected
}

/** All Patients page size (PAT-08 "Load More") */
const PAGE_SIZE = 6;
/** Search debounce (PAT-08) */
const SEARCH_DEBOUNCE_MS = 300;

const INTAKE_PERMISSIONS = ['admin:patients:phi:create', 'patientmr:phi:referral:create'] as PermissionString[];

/** `status` query value: the comma list, or `all` when every status is selected. */
export function toStatusParam(statuses: PatientStatusWire[]): string {
  return PATIENT_STATUS_FILTERS.every((f) => statuses.includes(f.value)) ? 'all' : statuses.join(',');
}

const PatientHome: React.FC<PatientHomeProps> = ({
  onShowPatientChart // Add back the destructured prop
}) => {
  const { selectedPatientId, setSelectedPatientId, setSelectedPatient, setPatientUnselected } = usePatient();
  const canViewAllPatients = useCanPerform('read:patients:phi:all');
  const canIntakeReferral = useHasAllPermissions(INTAKE_PERMISSIONS);
  // Phase 13 (ACC-16): owners/admins can load a sample patient from the empty list
  const canManageSubscription = useCanPerform('manage:subscription');
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);

  // For backwards compatibility with existing code
  const [myPatientsFormatted, setMyPatientsFormatted] = useState<Patient[]>([]);
  // My Patients search state
  const [myPatientsSearch, setMyPatientsSearch] = useState('');
  const [filteredMyPatients, setFilteredMyPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  // API state for All Patients section
  const [apiPatients, setApiPatients] = useState<PatientData[]>([]);
  const [apiPage, setApiPage] = useState(1);
  const [apiHasMore, setApiHasMore] = useState(false);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiLoadingMore, setApiLoadingMore] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiSearchQuery, setApiSearchQuery] = useState('');
  const debouncedSearch = useDebounce(apiSearchQuery.trim(), SEARCH_DEBOUNCE_MS);
  const [statusFilter, setStatusFilter] = useState<PatientStatusWire[]>(DEFAULT_PATIENT_STATUS_FILTER);
  const statusParam = toStatusParam(statusFilter);

  // Collapsible state
  const [myPatientsCollapsed, setMyPatientsCollapsed] = useState(false);
  const [allPatientsCollapsed, setAllPatientsCollapsed] = useState(false);

  // Refs for deduplication / stale responses
  const hasLoadedMyPatientsRef = useRef<boolean>(false);
  const listRequestRef = useRef(0);

  // Initialize component and set mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint is typically 640px
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    setLoading(false);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  /** Load page 1 (replace) for the current search + status filter */
  const loadFirstPage = useCallback(() => {
    const requestId = ++listRequestRef.current;
    setApiLoading(true);
    setApiError(null);
    fetchPatients(1, PAGE_SIZE, debouncedSearch, statusParam)
      .then((response) => {
        if (requestId !== listRequestRef.current) return;
        setApiPatients(response.patients);
        setApiPage(1);
        setApiHasMore(response.hasMore);
      })
      .catch((error: unknown) => {
        if (requestId !== listRequestRef.current) return;
        const message = error instanceof Error ? error.message : '';
        setApiError(message === 'Authentication token not found'
          ? 'Authentication error. Please log in again.'
          : 'Failed to load patients. Please try again.');
      })
      .finally(() => {
        if (requestId === listRequestRef.current) setApiLoading(false);
      });
  }, [debouncedSearch, statusParam]);

  useEffect(() => {
    if (allPatientsCollapsed || !canViewAllPatients) return;
    loadFirstPage();
  }, [loadFirstPage, allPatientsCollapsed, canViewAllPatients]);

  /** Append the next page (PAT-08 Load More) */
  const loadMore = () => {
    if (apiLoadingMore || !apiHasMore) return;
    const requestId = listRequestRef.current;
    const nextPage = apiPage + 1;
    setApiLoadingMore(true);
    fetchPatients(nextPage, PAGE_SIZE, debouncedSearch, statusParam)
      .then((response) => {
        if (requestId !== listRequestRef.current) return;
        setApiPatients((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...response.patients.filter((p) => !seen.has(p.id))];
        });
        setApiPage(nextPage);
        setApiHasMore(response.hasMore);
      })
      .catch(() => {
        if (requestId === listRequestRef.current) setApiError('Failed to load more patients. Please try again.');
      })
      .finally(() => setApiLoadingMore(false));
  };

  // Fetch My Patients data from API - only once on mount
  useEffect(() => {
    if (hasLoadedMyPatientsRef.current) {
      return;
    }
    hasLoadedMyPatientsRef.current = true;
    if (myPatientsCollapsed) {
      return;
    }

    fetchMyPatients()
      .then(patients => {
        // Transform PatientData to the format expected by the existing UI components
        const formattedPatients = patients.map(patient => ({
          id: patient.id,
          firstName: patient.firstName,
          lastName: patient.lastName,
          name: `${patient.firstName} ${patient.lastName}`,
          dob: patient.dob,
          gender: patient.gender,
          mrn: patient.mrn,
          status: normalizePatientStatus(patient.status),
          initials: `${patient.firstName.charAt(0)}${patient.lastName.charAt(0)}`,
          profilePictureUrl: patient.profilePictureUrl || null,
          insuranceSummary: patient.insuranceSummary || null,
          recertOverdue: !!patient.recertOverdue
        }));

        setMyPatientsFormatted(formattedPatients);
        setFilteredMyPatients(formattedPatients);
      })
      .catch(() => {
        console.error('Failed to fetch my patients');
        hasLoadedMyPatientsRef.current = false;
        setMyPatientsFormatted([]);
        setFilteredMyPatients([]);
      });
  }, [myPatientsCollapsed]);

  // My Patients search functionality
  useEffect(() => {
    if (myPatientsSearch.trim() === '') {
      setFilteredMyPatients(myPatientsFormatted);
    } else {
      const query = myPatientsSearch.toLowerCase();
      setFilteredMyPatients(
        myPatientsFormatted.filter(p =>
          (p.name?.toLowerCase() || '').includes(query) ||
          (p.firstName?.toLowerCase() || '').includes(query) ||
          (p.lastName?.toLowerCase() || '').includes(query) ||
          p.mrn.toLowerCase().includes(query)
        )
      );
    }
  }, [myPatientsSearch, myPatientsFormatted]);

  // Function to format date from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, dayPart] = dateStr.split('-');
    const day = dayPart ? dayPart.substring(0, 2) : '';
    return `${(month || '').padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  };

  // Helper to convert Patient to PatientData
  const patientToPatientData = (patient: Patient): PatientData => ({
    id: patient.id.toString(),
    tenantId: '',
    mrn: patient.mrn,
    firstName: patient.firstName || (patient.name ? patient.name.split(' ')[0] : ''),
    lastName: patient.lastName || (patient.name ? patient.name.split(' ').slice(1).join(' ') : ''),
    dob: patient.dob,
    email: '',
    gender: patient.gender,
    phoneNumber: '',
    status: normalizePatientStatus(patient.status),
    profilePictureUrl: patient.profilePictureUrl || null,
    insuranceSummary: patient.insuranceSummary || null,
    recertOverdue: !!patient.recertOverdue,
  });

  // Helper function to handle patient selection consistently across the component
  const selectPatient = (patient: Patient | PatientData | null) => {
    if (!patient) {
      setSelectedPatientId(null);
      setSelectedPatient(null);
      setPatientUnselected(true);
      return;
    }

    setSelectedPatientId(patient.id);
    const patientData = 'firstName' in patient && 'lastName' in patient && 'email' in patient
      ? patient as PatientData
      : patientToPatientData(patient as Patient);
    setSelectedPatient(patientData);
  };

  /** Load the sample patient and open its chart; SAMPLE_EXISTS opens the existing one when the ID is known. */
  const loadSamplePatient = async () => {
    setSampleLoading(true);
    setSampleError(null);
    try {
      openPatientChartById(await SubscriptionService.loadSamplePatient());
    } catch (err) {
      const existingId = getApiErrorCode(err) === 'SAMPLE_EXISTS' && isApiError(err) ? err.details?.patientId : undefined;
      if (typeof existingId === 'string' && existingId) {
        openPatientChartById(existingId);
      } else {
        setSampleError(describeApiError(err, 'Unable to load the sample patient.'));
      }
    } finally {
      setSampleLoading(false);
    }
  };

  const sampleOffer = canManageSubscription && !debouncedSearch ? (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={loadSamplePatient}
        disabled={sampleLoading}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
      >
        <UserPlus size={16} aria-hidden="true" /> {sampleLoading ? 'Loading...' : 'Load sample patient'}
      </button>
      <p className="text-xs text-gray-500">Explore with made-up data (not real PHI). It never counts as an active patient.</p>
      {sampleError && <p className="text-xs text-red-700" role="alert">{sampleError}</p>}
    </div>
  ) : undefined;

  /** Intake success or "Open existing chart": select the patient and open the chart */
  const openPatientChart = (patient: PatientData) => {
    setIntakeOpen(false);
    selectPatient(patient);
    onShowPatientChart?.(patient);
  };

  return (
    <div className="w-full">
      {loading ? (
        <LoadingSpinner className="h-64" />
      ) : (
        <div className="w-full">
          <div className="p-4 pb-2 flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-2xl sm:text-2xl font-semibold text-gray-800">Patient Dashboard</h1>
            {canIntakeReferral && (
              <button
                type="button"
                onClick={() => setIntakeOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 min-h-[44px] sm:min-h-0"
              >
                <UserPlus size={16} aria-hidden="true" /> New referral
              </button>
            )}
          </div>
          <div className="px-4 pb-4">
            {/* My Patients Section */}
            <div className="bg-white rounded-lg shadow-md p-3 mb-4">
              <div className="flex items-center justify-between cursor-pointer select-none" onClick={() => setMyPatientsCollapsed(v => !v)}>
                <h2 className="text-xl font-semibold text-gray-700 px-1">My Patients</h2>
                {myPatientsCollapsed ? (
                  <ChevronDown className="text-gray-500" size={22} />
                ) : (
                  <ChevronUp className="text-gray-500" size={22} />
                )}
              </div>
              {!myPatientsCollapsed && (
                <>
                  <div className="mt-3 mb-3 flex items-center">
                    <div className="relative w-full sm:w-64">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Search my patients..."
                        className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                        value={myPatientsSearch}
                        onChange={e => setMyPatientsSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mt-2">
                    {filteredMyPatients.map(patient => (
                      <div
                        key={patient.id}
                        className={`group relative cursor-pointer ${selectedPatientId === patient.id
                          ? 'bg-slate-100 border-slate-400'
                          : 'bg-slate-50 border-slate-200'
                        } border rounded-lg px-3 py-2 flex items-center hover:shadow transition-shadow`}
                        onClick={() => {
                          // Toggle selection - if this patient is already selected, unselect it
                          if (selectedPatientId === patient.id) {
                            selectPatient(null);
                          } else {
                            selectPatient(patient);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={selectedPatientId === patient.id}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="relative flex-shrink-0">
                              <PatientAvatar
                                patientId={patient.id.toString()}
                                name={patient.name}
                                profilePictureUrl={patient.profilePictureUrl}
                                size={isMobile ? 'lg' : 'md'}
                              />
                              {selectedPatientId === patient.id && (
                                <div className="absolute -top-0.5 -right-0.5 bg-white rounded-full p-0.5 shadow-sm">
                                  <svg className="h-3.5 w-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-800 truncate leading-tight text-left text-base">{patient.name}</div>
                              <div className="text-slate-600 text-left mt-0.5 text-xs">
                                DOB: {formatDate(patient.dob)} • {patient.gender}
                              </div>
                              <div className="text-slate-500 text-left mt-0.5 text-xs">
                                MRN: {patient.mrn}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <StatusPill status={patient.status || 'INACTIVE'} />
                                <PayerBadges summary={patient.insuranceSummary} />
                                <RecertOverdueBadge overdue={patient.recertOverdue} />
                              </div>
                            </div>
                          </div>
                          {/* Actions section */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering the card click
                              selectPatient(patient);
                              if (onShowPatientChart) {
                                onShowPatientChart(patient);
                              }
                            }}
                            className={`flex-shrink-0 ml-2 rounded transition-colors flex items-center justify-center ${isMobile ? 'px-4 py-2.5 text-sm min-h-[44px] bg-slate-200 text-slate-700 hover:bg-slate-300' : 'px-3 py-1 text-sm bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                            aria-label="View patient chart"
                          >
                            <img src={require("../../assets/images/icon_dark.png")} alt="Care" className={`mr-1 ${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                            Care
                          </button>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* All Patients Section */}
            {canViewAllPatients && (
              <AllPatientsList
                patients={apiPatients}
                selectedPatientId={selectedPatientId}
                expanded={!allPatientsCollapsed}
                onToggleExpand={() => setAllPatientsCollapsed(v => !v)}
                loading={apiLoading}
                error={apiError}
                hasMore={apiHasMore}
                loadingMore={apiLoadingMore}
                onLoadMore={loadMore}
                searchQuery={apiSearchQuery}
                onSearchChange={setApiSearchQuery}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                onRefresh={loadFirstPage}
                onDismissError={() => setApiError(null)}
                onSelectPatient={(patient) => selectPatient(patient)}
                onShowPatientChart={(patient) => onShowPatientChart?.(patient)}
                topBanner={<CapUsageBanner />}
                emptyStateAction={sampleOffer}
              />
            )}
            {/* End All Patients Section */}
          </div>
        </div>
      )}
      {canIntakeReferral && (
        <IntakeDialog
          open={intakeOpen}
          onCancel={() => setIntakeOpen(false)}
          onOpenPatient={openPatientChart}
        />
      )}
    </div>
  );
};

export default PatientHome;
